import { BookingRole } from "@/lib/types";

// Octarnal brand lime — the tech-sphere uses this for everyone (not per-member
// accents) so the viz reads as one brand across all profiles.
export const BRAND = "#b2d127";

// The dark surface the site's cards sit on — the `--card-foreground` token,
// repeated here as a literal because it has to cross into Cal's iframe, where
// our stylesheet (and therefore the CSS variable) does not exist.
export const SURFACE = "#212129";

// The home-page section holding the inline Cal booker. Every "Book Now" in the
// site scrolls here rather than opening a popup, so the id is shared rather
// than repeated as a string in each caller.
export const BOOKING_SECTION_ID = "book-session";

// Single source of truth for the 3D tech-sphere "physics". Tune the feel here
// without touching the loop. Distances are fractions of the sphere radius unless
// the name says px; times in s; angles in rad.
export const NETWORK = {
  // Stage ----------------------------------------------------------------
  maxSize: 720, // px cap for the square stage on large screens
  edgeMargin: 88, // px breathing room; leaves room for the hover-enlarge
  radiusFill: 1.0, // resting sphere radius as fraction of the available half-size
  perspective: 4, // camera distance (in radii) — bigger = flatter depth

  // Density — pad the stack with plain dots so the shell fills into a full,
  // round molecule; ~half the visible nodes end up being real labelled tech.
  fillFactor: 2, // total nodes ≈ labelled * fillFactor
  fillMin: 46,
  fillMax: 78,
  neighborLinks: 5, // nearest neighbours wired per node → denser, fewer gaps
  importantRatio: 0.32, // leading share of the stack rendered larger + always readable

  // Rotation -------------------------------------------------------------
  spin: 0.14, // auto Y-spin, rad/s
  wobble: 0.12, // gentle X nod amplitude (rad)

  // Surface undulation (radial jitter, fraction of radius) — subtle life -----
  driftMin: 0.012,
  driftMax: 0.045,

  // Entrance -------------------------------------------------------------
  entranceStart: 0.2, // delay before the first node leaves the center
  entranceSpread: 0.8, // stagger window across all nodes
  entranceDur: 0.9, // travel time center → shell

  // Pointer tilt ---------------------------------------------------------
  maxTilt: 0.35, // rad the sphere leans toward the cursor
  tiltSpring: 0.06,

  // Hover — the WHOLE sphere enlarges when the cursor is over the stage so a
  // visitor can read the entire stack.
  hoverScale: 1.15,
  hoverSpring: 0.09,

  // Depth cues -----------------------------------------------------------
  depthScaleMin: 0.55, // back-of-sphere node scale
  depthScaleMax: 1.15, // front node scale
  depthOpacityMin: 0.28,
  depthOpacityMax: 1,
} as const;

// Badge text per booking role. A value, so it lives here rather than in
// lib/types, which holds only type declarations.
export const ROLE_BADGE: Record<BookingRole, string> = {
  host: "Host",
  you: "You",
  guest: "Guest",
};
